import { extend, debounce } from "./core";
import { el } from "./dom";
import { getScrollbarWidth, isIE, getScrollbarHeight, locateNodeByClassName } from "./html";
import { IHandlers, anyFunction } from "./types";

interface IScrollViewConfig {
	enable: boolean;
	autoHide: boolean;
	timeout: number;
	scrollHandler: anyFunction;
}

export const scrollViewConfig: IScrollViewConfig = {
	enable: false,
	autoHide: true,
	timeout: 1000,
	scrollHandler: () => {},
};

export interface IScrollView {
	config: IScrollViewConfig;
	enable(): void;
	disable(): void;
	render(nods?: any[]): any;
	update(): void;
}

export class ScrollView implements IScrollView {
	public config: IScrollViewConfig;

	private _getRootView;
	private _scrollYTop: number;
	private _scrollXLeft: number;
	private _runnerYTop: number;
	private _runnerXLeft: number;
	private _runnerHeight: number;
	private _runnerWidth: number;
	private _visibleYArea: number;
	private _visibleXArea: number;
	private _scrollWidth: number;
	private _scrollHeight: number;
	private _wheelName: string;
	private _handlers: IHandlers;
	private _autoHideFunc: anyFunction;
	private _uid: string;

	constructor(getRootView, config = {}) {
		this.config = extend(
			{
				enable: scrollViewConfig.enable,
				autoHide: scrollViewConfig.autoHide,
				timeout: scrollViewConfig.timeout,
				scrollHandler: scrollViewConfig.scrollHandler,
			},
			config
		);

		this._wheelName = isIE() ? "onmousewheel" : "onwheel";

		this._getRootView = getRootView;
		this._scrollYTop = this._scrollXLeft = this._runnerYTop = this._runnerXLeft = this._runnerHeight = this._runnerWidth = 0;
		this._visibleYArea = this._visibleXArea = 1;

		this._scrollWidth = getScrollbarWidth();
		this._scrollHeight = getScrollbarHeight();

		this._handlers = {
			onscroll: e => {
				this.config.scrollHandler(e);
				this.update();
			},
			[this._wheelName]: e => {
				const isY = !!locateNodeByClassName(e.target, "y-scroll");
				e.preventDefault();

				const sign = (e.deltaY || -e.wheelDelta) > 0 ? 1 : -1;
				const delta = sign * 40;
				const { area } = this._getRefs();
				if (isY) {
					const maxBottom = area.scrollHeight - this._runnerHeight;
					const newScrollTop = this._scrollYTop + delta;

					if (newScrollTop < 0) {
						area.scrollTop = 0;
					} else if (newScrollTop > maxBottom) {
						area.scrollTop = maxBottom;
					} else {
						area.scrollTop = newScrollTop;
					}
				} else {
					const maxRight = area.scrollWidth - this._runnerWidth;
					const newScrollLeft = this._scrollXLeft + delta;

					if (newScrollLeft < 0) {
						area.scrollLeft = 0;
					} else if (newScrollLeft > maxRight) {
						area.scrollLeft = maxRight;
					} else {
						area.scrollLeft = newScrollLeft;
					}
				}

				this.update();
			},
			onmousedownRunner: mouseDownEv => {
				mouseDownEv.preventDefault();
				const isY = !!locateNodeByClassName(mouseDownEv.target, "y-scroll");

				const { area, runnerY, runnerX } = this._getRefs();

				const rect = area.getBoundingClientRect();

				const top = rect.top + window.pageYOffset;
				const bottom = rect.bottom + window.pageYOffset;
				const maxBottom = area.scrollHeight - this._runnerHeight;
				const deltaY = mouseDownEv.pageY - runnerY.getBoundingClientRect().top - window.pageYOffset;

				const left = rect.left + window.pageXOffset;
				const right = rect.right + window.pageXOffset;
				const maxRight = area.scrollWidth - this._runnerWidth;
				const deltaX = mouseDownEv.pageX - runnerX.getBoundingClientRect().left - window.pageXOffset;

				const mouseMove = (e: MouseEvent) => {
					if (isY) {
						const y = e.pageY - deltaY;
						if (y <= top) {
							area.scrollTop = 0;
						} else if (y > bottom) {
							area.scrollTop = maxBottom;
						} else {
							area.scrollTop = (y - top) / this._visibleYArea;
						}
					} else {
						const x = e.pageX - deltaX;
						if (x <= left) {
							area.scrollLeft = 0;
						} else if (x > right) {
							area.scrollLeft = maxRight;
						} else {
							area.scrollLeft = (x - left) / this._visibleXArea;
						}
					}

					this.update();
				};

				const mouseUp = () => {
					document.removeEventListener("mousemove", mouseMove);
					document.removeEventListener("mouseup", mouseUp);
					document.body.classList.remove("dhx-no-select");
				};

				document.body.classList.add("dhx-no-select");
				document.addEventListener("mousemove", mouseMove);
				document.addEventListener("mouseup", mouseUp);
			},
			onmousedownArea: e => {
				if (locateNodeByClassName(e, "scroll-runner")) return;
				e.preventDefault();
				const isY = !!locateNodeByClassName(e.target, "y-scroll");

				const { area, runnerY, runnerX } = this._getRefs();

				if (isY) {
					area.scrollTop += (e.pageY - runnerY.getBoundingClientRect().top) / this._visibleYArea;
				} else {
					area.scrollLeft += (e.pageX - runnerX.getBoundingClientRect().left) / this._visibleXArea;
				}

				this.update();
			},
			onmouseenter: e => {
				if (locateNodeByClassName(e, "scroll-runner")) return;
				const refs = this._getRefs();
				if (!refs) {
					return;
				}

				const isY = !!locateNodeByClassName(e.target, "y-scroll");
				const { areaX, areaY } = refs;

				if (isY && this._runnerHeight > 0) {
					areaY.style.background = "#eee";
				} else if (!isY && this._runnerWidth > 0) {
					areaX.style.background = "#eee";
				}
			},
			onmouseleave: e => {
				if (locateNodeByClassName(e, "scroll-runner")) return;
				const refs = this._getRefs();
				if (!refs) {
					return;
				}

				const isY = !!locateNodeByClassName(e.target, "y-scroll");
				const { areaX, areaY } = refs;

				if (isY && this._runnerHeight > 0) {
					areaY.style.background = "transparent";
				} else if (!isY && this._runnerWidth > 0) {
					areaX.style.background = "transparent";
				}
			}
		};
	}
	enable() {
		this.config.enable = true;
		this._getRootView().redraw();
	}
	disable() {
		this.config.enable = false;
		this._getRootView().redraw();
	}
	render(element, uid = "") {
		if (!this.config.enable || !element.length) {
			return element;
		}
		if (uid) this._uid = uid;

		const scrollView = this.config.enable
			? [
					el(
						".y-scroll",
						{
							[this._wheelName]: this._handlers[this._wheelName],
							_ref: uid ? `scroll-y-area-${uid}` : `scroll-y-area`,
							onmousedown: this._handlers.onmousedownArea,
							onmouseenter: this._handlers.onmouseenter,
							onmouseleave: this._handlers.onmouseleave,
							style: {
								width: "6px",
								height: "100%",
								right: 0,
								top: 0,
								position: "absolute",
							},
						},
						[
							el(`.scroll-runner`, {
								_ref: uid ? `scroll-y-runner-${uid}` : "scroll-y-runner",
								onmousedown: this._handlers.onmousedownRunner,
								style: {
									height: this._runnerHeight + "px",
									top: this._runnerYTop,
								},
							}),
						]
					),
					el(
						".x-scroll",
						{
							[this._wheelName]: this._handlers[this._wheelName],
							_ref: uid ? `scroll-x-area-${uid}` : `scroll-x-area`,
							onmousedown: this._handlers.onmousedownArea,
							onmouseenter: this._handlers.onmouseenter,
							onmouseleave: this._handlers.onmouseleave,
							style: {
								width: "100%",
								height: "6px",
								left: 0,
								bottom: 0,
								position: "absolute",
							},
						},
						[
							el(`.scroll-runner`, {
								_ref: uid ? `scroll-x-runner-${uid}` : "scroll-x-runner",
								onmousedown: this._handlers.onmousedownRunner,
								style: {
									width: this._runnerWidth + "px",
									left: this._runnerXLeft,
								},
							}),
						]
					),
			  ]
			: null;

		return el(
			".scroll-view-wrapper",
			[
				el(
					".scroll-view",
					{
						onscroll: this._handlers.onscroll,
						_ref: uid ? `scroll-view-${uid}` : "scroll-view",
						_hooks: {
							didInsert: () => {
								this.update();
							},
							didRecycle: () => {
								this.update();
							},
						},
						style: {
							width: `calc(100% + ${this._scrollWidth}px)`,
							height: `calc(100% + ${this._scrollHeight}px)`,
						},
					},
					element
				),
			].concat(scrollView)
		);
	}
	update() {
		const refs = this._getRefs();
		if (!refs) {
			return;
		}

		const { area, areaX, areaY, runnerY, runnerX } = refs;

		this._visibleYArea = area.clientHeight / area.scrollHeight;
		this._visibleXArea = area.clientWidth / area.scrollWidth;
		this._scrollYTop = area.scrollTop;
		this._scrollXLeft = area.scrollLeft;
		this._runnerYTop = this._scrollYTop * this._visibleYArea;
		this._runnerXLeft = this._scrollXLeft * this._visibleXArea;

		this._runnerHeight = this._visibleYArea < 1 ? area.clientHeight * this._visibleYArea : 0;
		this._runnerWidth = this._visibleXArea < 1 ? area.clientWidth * this._visibleXArea : 0;

		const initialTop = runnerY.style.top;
		const initialLeft = runnerX.style.left;

		// update dom
		runnerY.style.opacity = 1;
		runnerY.style.top = this._runnerYTop + "px";
		runnerY.style.height = this._runnerHeight + "px";

		runnerX.style.opacity = 1;
		runnerX.style.left = this._runnerXLeft + "px";
		runnerX.style.width = this._runnerWidth + "px";

		if (initialTop !== runnerY.style.top) {
			areaY.style.opacity = 0.9;
			areaY.style.width = "10px";
		}
		if (initialLeft !== runnerX.style.left) {
			areaX.style.opacity = 0.9;
			areaX.style.height = "10px";
		}

		if (this.config.autoHide) {
			!this._autoHideFunc &&
				(this._autoHideFunc = debounce(() => {
					runnerY.style.opacity = 0;
					areaY.style.width = "6px";

					runnerX.style.opacity = 0;
					areaX.style.height = "6px";
				}, this.config.timeout));
		} else {
			this._autoHideFunc = debounce(() => {
				areaY.style.width = "6px";
				areaX.style.height = "6px";
			}, this.config.timeout);
		}
		this._autoHideFunc();
	}
	private _getRefs() {
		const rootView = this._getRootView();

		const refsCheck = !!(
			rootView.refs["scroll-view"] &&
			(rootView.refs["scroll-x-runner"] || rootView.refs["scroll-y-runner"])
		);

		const refsIdCheck = !!(
			this._uid &&
			rootView.refs[`scroll-view-${this._uid}`] &&
			(rootView.refs[`scroll-x-runner-${this._uid}`] || rootView.refs[`scroll-y-runner-${this._uid}`])
		);

		if (rootView.refs) {
			if (refsCheck) {
				return {
					area: rootView.refs["scroll-view"].el,
					areaY: rootView.refs["scroll-y-area"].el,
					areaX: rootView.refs["scroll-x-area"].el,
					runnerY: rootView.refs["scroll-y-runner"].el,
					runnerX: rootView.refs["scroll-x-runner"].el,
				};
			} else if (refsIdCheck) {
				return {
					area: rootView.refs[`scroll-view-${this._uid}`].el,
					areaY: rootView.refs[`scroll-y-area-${this._uid}`].el,
					areaX: rootView.refs[`scroll-x-area-${this._uid}`].el,
					runnerY: rootView.refs[`scroll-y-runner-${this._uid}`].el,
					runnerX: rootView.refs[`scroll-x-runner-${this._uid}`].el,
				};
			}
		}
	}
}
